interface Category {
  key: string;
  label: string;
  min: number;
  max: number;
}
interface Gauge {
  currentBmi: number;
  startingBmi: number | null;
  change: number | null;
  category: Category;
  nextThreshold: { bmi: number; label: string; weightLb: number; lbToGo: number } | null;
  categories: Category[];
}

// BMI display window. Markers are clamped into this range.
const LO = 15;
const HI = 45;
const BAND_COLORS: Record<string, string> = {
  underweight: "#5b8def",
  healthy: "#4fd1a0",
  overweight: "#e8c15a",
  obese1: "#e89b4f",
  obese2: "#e07a52",
  obese3: "#e06a6a"
};

function pct(bmi: number): number {
  const clamped = Math.max(LO, Math.min(HI, bmi));
  return ((clamped - LO) / (HI - LO)) * 100;
}

export function BmiGauge({ gauge }: { gauge: Gauge }) {
  const current = gauge.currentBmi;
  const currentX = pct(current);
  const startX = gauge.startingBmi !== null ? pct(gauge.startingBmi) : null;
  const improving = gauge.change !== null && gauge.change < 0;

  return (
    <div className="card card-pad-lg">
      <div className="row-between">
        <div>
          <div className="stat-label">Current BMI</div>
          <div style={{ fontSize: "var(--text-3xl)", fontWeight: 700, lineHeight: 1.1 }}>
            {current}
          </div>
          <div className="row" style={{ gap: "var(--space-2)", marginTop: 4 }}>
            <span
              className="badge"
              style={{ background: `${BAND_COLORS[gauge.category.key]}22`, color: BAND_COLORS[gauge.category.key], borderColor: "transparent" }}
            >
              {gauge.category.label}
            </span>
            {gauge.change !== null && (
              <span className={improving ? "badge badge-success" : "badge badge-muted"}>
                {gauge.change > 0 ? "+" : ""}
                {gauge.change} since start
              </span>
            )}
          </div>
        </div>
        {gauge.startingBmi !== null && (
          <div style={{ textAlign: "right" }}>
            <div className="stat-label">Started</div>
            <div style={{ fontSize: "var(--text-xl)", fontWeight: 600 }} className="dim">
              {gauge.startingBmi}
            </div>
          </div>
        )}
      </div>

      {/* The gauge */}
      <div style={{ marginTop: "var(--space-6)", position: "relative" }}>
        <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ width: "100%", height: 26, display: "block", borderRadius: 6, overflow: "hidden" }}>
          {gauge.categories.map((c) => {
            const x0 = pct(c.min);
            const x1 = pct(c.max === Infinity ? HI : c.max);
            const w = Math.max(0, x1 - x0);
            if (w <= 0) return null;
            return (
              <rect key={c.key} x={x0} y={0} width={w} height={10} fill={BAND_COLORS[c.key]} opacity={c.key === gauge.category.key ? 0.85 : 0.32} />
            );
          })}
        </svg>

        {/* Starting marker (ghost) */}
        {startX !== null && (
          <div style={{ position: "absolute", top: -4, left: `${startX}%`, transform: "translateX(-50%)" }}>
            <div style={{ width: 2, height: 34, background: "var(--text-tertiary)", opacity: 0.6, margin: "0 auto" }} />
          </div>
        )}
        {/* Current marker */}
        <div style={{ position: "absolute", top: -7, left: `${currentX}%`, transform: "translateX(-50%)", transition: "left 400ms var(--ease)" }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "var(--surface-0)", border: `3px solid ${BAND_COLORS[gauge.category.key]}`, boxShadow: `0 0 12px ${BAND_COLORS[gauge.category.key]}88`, margin: "0 auto" }} />
          <div style={{ width: 2, height: 24, background: BAND_COLORS[gauge.category.key], margin: "0 auto" }} />
        </div>
      </div>

      {/* Scale labels */}
      <div className="row-between dim text-xs" style={{ marginTop: "var(--space-4)" }}>
        <span>15</span>
        <span>Healthy 18.5–25</span>
        <span>45+</span>
      </div>

      {gauge.nextThreshold && (
        <div className="alert alert-info text-sm" style={{ marginTop: "var(--space-4)", marginBottom: 0 }}>
          About <strong>{gauge.nextThreshold.lbToGo} lb</strong> to cross into the{" "}
          <strong>{gauge.nextThreshold.label}</strong> range (BMI {gauge.nextThreshold.bmi}).
        </div>
      )}
      <p className="dim text-xs mt-4" style={{ marginBottom: 0 }}>
        BMI is a screening number from height and weight — not a measure of body fat or health on its own.
      </p>
    </div>
  );
}
